import { SheetModeRenderOrders } from "@/modes/sheet-mode/sheet-mode-render-orders";
import { useAppSelector } from "@/store/store-hooks";
import { selectOdometryWaypointSpacing } from "@/store/view-options/view-options-selectors";
import {
  blue,
  cyan,
  isMapPlaceholdersUpdated,
  MapWaypointsRenderer,
  MapWaypointsRendererRef,
  PrecisePoints,
  SinglePointGeometry,
  useClipFrustum,
  useMapPlaceholderTextures,
  useSvg,
} from "@faro-lotv/app-component-toolbox";
import {
  isAncestor,
  MapPlaceholders,
  PlaceholdersTexture,
} from "@faro-lotv/lotv";
import { useCursor } from "@react-three/drei";
import { ThreeEvent } from "@react-three/fiber";
import { DomEvent } from "@react-three/fiber/dist/declarations/src/core/events";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  AddEquation,
  Color,
  CustomBlending,
  Group,
  Intersection,
  OneFactor,
  OneMinusSrcAlphaFactor,
  Plane,
  Points,
  Raycaster,
  Vector3,
} from "three";
import EndPointSvg from "./VM_EndPoint.svg?url";
import StartPointSvg from "./VM_StartPoint.svg?url";
import PointSvg from "./VM_WaypointDefault.svg?url";
import HoverSvg from "./VM_WaypointHover.svg?url";
import LockedSvg from "./VM_WaypointLocked.svg?url";
import { useSubSamplePlaceholders } from "./use-subsample-placeholders";

/** Default pixel size of the path waypoints */
const PATH_WAYPOINTS_BASE_SIZE = 8;

const LOCKED_WAYPOINT_SIZE = 16;
const LOCKED_HOVERED_WAYPOINT_SIZE = 24;

const START_END_POINT_SIZE = 8;
const START_END_POINT_HOVERED_SIZE = 12;

/** The multiplication factor for the size of an hovered placeholder */
const PLACEHOLDER_HOVER_FACTOR = 1.5;

const LOCKED_WAYPOINTS_LABEL = "Locked waypoints";

const POI_HI_RES_LABEL = "POI high res label";
const DEFAULT_HI_RES_POI_SIZE = 20;
const HOVERED_HI_RES_POI_SIZE = 30;

/** The kind of waypoint of a path, each kind being rendered on its own */
type WaypointKind = "promoted" | "other" | "startEnd";

interface CommonProps {
  /** Which placeholder should currently be hovered */
  hoveredId?: number;

  /** Whether the path has bee hovered by the user */
  isPathHovered: boolean;

  /** Callback executed when a placeholder is clicked */
  onPlaceholderClick?(id: number): void;

  /** Callback when a context menu is requested for a waypoint */
  onPlaceholderContextMenu?(index: number): void;
}

interface OdometryPathPlaceholdersProps extends CommonProps {
  /** The set of placeholders to render */
  positions: Vector3[];

  /** Whether to show the auto-promoted waypoints */
  showPromoted: boolean;

  /** Whether to show the dense "other" waypoints and the start/end placeholders */
  showOther: boolean;

  /** Whether to split middle waypoints into promoted and dense ones */
  enableAutoPromotion: boolean;

  /** List of indices within the 'position' array that represent points of interest */
  poiIndices: number[];

  /** A list containing for each placeholder a flag specifying if it should be considered "locked" */
  locked: boolean[];

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /**
   * Callback executed when the hovered placeholder changes
   *
   * @param id the index of the hovered placeholder in the positions list
   * @param isPromoted whether the hovered placeholder is an auto-promoted waypoint
   */
  onHoveredPlaceholderChanged(
    id: number | undefined,
    isPromoted: boolean,
  ): void;
}

/**
 * @param intersection the intersected object
 * @returns the placeholder index of the intersected object
 */
export function computeRaycastedIndex(
  intersection?: Intersection,
): number | undefined {
  // The first and last placeholders (Points) are explicitly named with their index.
  if (intersection?.object.name) {
    const namedIndex = Number.parseInt(intersection.object.name, 10);

    // A hovered keyframe reports its MapPlaceholders group, whose non-numeric name must be ignored.
    if (!Number.isNaN(namedIndex)) {
      return namedIndex;
    }
  }
  // Otherwise we hit the keyframes buffer: the index is the point index + 1 for the starting point.
  if (intersection?.index !== undefined) {
    return intersection.index + 1;
  }
}

/**
 * @param raycaster to cast a ray on the placeholders
 * @param group that contains the OdometryPathPlaceholders
 * @returns the index of the hit placeholder or undefined
 */
export function raycastOnPlaceholdersGroup(
  raycaster: Raycaster,
  group: Group,
): number | undefined {
  const points: Array<MapPlaceholders | Points> = [];
  group.traverse((o) => {
    if (o instanceof MapPlaceholders || o instanceof Points) {
      points.push(o);
    }
  });
  const hit = raycaster.intersectObjects(points)[0];
  return computeRaycastedIndex(hit);
}

/**
 * @returns the placeholders for an odometry path
 */
export function OdometryPathPlaceholders({
  positions,
  showPromoted,
  showOther,
  enableAutoPromotion,
  poiIndices,
  locked,
  hoveredId,
  clippingPlanes,
  isPathHovered,
  onHoveredPlaceholderChanged,
  onPlaceholderClick,
  onPlaceholderContextMenu,
}: OdometryPathPlaceholdersProps): JSX.Element | null {
  const [start, end, rest] = useMemo(
    () => [positions.at(0), positions.at(-1), positions.slice(1, -1)],
    [positions],
  );

  const restLocked = useMemo(() => locked.slice(1, -1), [locked]);

  const restPois = useMemo(() => {
    if (poiIndices.length === 0) {
      return [];
    }
    const ret = [...poiIndices];
    if (poiIndices[0] === 0) {
      ret.shift();
    }
    if (poiIndices[poiIndices.length - 1] === positions.length - 1) {
      ret.pop();
    }
    for (let i = 0; i < ret.length; i++) {
      ret[i]--;
    }
    return ret;
  }, [positions, poiIndices]);

  const groupRef = useRef<Group>(null);

  const hoveredKindRef = useRef<WaypointKind | undefined>(undefined);

  const reportHovered = useCallback(
    (id: number | undefined, kind: WaypointKind) => {
      // Ignore a "pointer left" coming from a kind of waypoint that is not the current hover owner
      if (id === undefined && hoveredKindRef.current !== kind) return;
      hoveredKindRef.current = id === undefined ? undefined : kind;
      onHoveredPlaceholderChanged(id, kind === "promoted");
    },
    [onHoveredPlaceholderChanged],
  );

  // The middle waypoints report their own hover, this only handles the start and end placeholders
  const updateHovered = useCallback(
    (ev: ThreeEvent<DomEvent>) => {
      ev.stopPropagation();
      const point = ev.intersections.find(
        (i) =>
          i.object instanceof Points &&
          i.object.name !== "" &&
          groupRef.current &&
          isAncestor(i.object, groupRef.current),
      );
      reportHovered(computeRaycastedIndex(point), "startEnd");
    },
    [reportHovered],
  );

  if (!start || !end) {
    return null;
  }

  return (
    <group
      ref={groupRef}
      position-y={0.1}
      onPointerEnter={updateHovered}
      onPointerMove={updateHovered}
      onPointerLeave={updateHovered}
    >
      {rest.length > 0 && (showPromoted || showOther) && (
        <OtherPlaceholders
          positions={rest}
          references={[start, end]}
          locked={restLocked}
          poiIndices={restPois}
          hoveredId={hoveredId}
          clippingPlanes={clippingPlanes}
          onPlaceholderClick={onPlaceholderClick}
          onPlaceholderContextMenu={onPlaceholderContextMenu}
          onHoveredPlaceholderChanged={reportHovered}
          renderOrder={SheetModeRenderOrders.PathWaypoints}
          isPathHovered={isPathHovered}
          showPromoted={showPromoted}
          showOther={showOther}
          enableAutoPromotion={enableAutoPromotion}
        />
      )}

      {showOther && (
        <StartEndPlaceholders
          position={start}
          index={0}
          hoveredId={hoveredId}
          isPathHovered={isPathHovered}
          clippingPlanes={clippingPlanes}
          onPlaceholderClick={onPlaceholderClick}
          onPlaceholderContextMenu={onPlaceholderContextMenu}
        />
      )}

      {showOther && (
        <StartEndPlaceholders
          position={end}
          index={positions.length - 1}
          hoveredId={hoveredId}
          isPathHovered={isPathHovered}
          clippingPlanes={clippingPlanes}
          onPlaceholderClick={onPlaceholderClick}
          onPlaceholderContextMenu={onPlaceholderContextMenu}
        />
      )}
    </group>
  );
}

interface StartEndPlaceholdersProps extends CommonProps {
  /** The position of the placeholder */
  position: Vector3;

  /** The index of this placeholder in the list */
  index: number;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];
}

/**
 * @returns the placeholders to be used at the start or end of a path
 */
function StartEndPlaceholders({
  position,
  index,
  hoveredId,
  isPathHovered,
  clippingPlanes,
  onPlaceholderClick,
  onPlaceholderContextMenu,
}: StartEndPlaceholdersProps): JSX.Element | null {
  const texture = useSvg(index === 0 ? StartPointSvg : EndPointSvg, {
    width: 256,
    height: 256,
    generateMipmaps: true,
  });

  const onClick = useCallback(
    (ev: ThreeEvent<DomEvent>) => {
      if (onPlaceholderClick) {
        ev.stopPropagation();
        onPlaceholderClick(index);
      }
    },
    [onPlaceholderClick, index],
  );

  const onContextMenu = useCallback(
    (ev: ThreeEvent<DomEvent>) => {
      if (onPlaceholderContextMenu) {
        ev.stopPropagation();
        onPlaceholderContextMenu(index);
      }
    },
    [onPlaceholderContextMenu, index],
  );

  const isHovered = hoveredId === index;

  useCursor(isHovered);

  const clipFrustum = useClipFrustum(clippingPlanes);
  const isVisible = useMemo(
    () => !clipFrustum || clipFrustum.containsPoint(position),
    [position, clipFrustum],
  );

  const [pointObject, setPointObject] = useState<Points | null>(null);
  useEffect(() => {
    if (!pointObject) return;

    Object.assign(pointObject.material, {
      // Prevent gray outlines when using mipmaps
      // https://github.com/mrdoob/three.js/issues/14668
      blending: CustomBlending,
      blendEquation: AddEquation,
      blendSrc: OneFactor,
      blendDst: OneMinusSrcAlphaFactor,
      blendSrcAlpha: OneFactor,
      blendDstAlpha: OneMinusSrcAlphaFactor,
      // Also needed -- otherwise you still get gray outlines
      premultipliedAlpha: false,
    });
  }, [pointObject]);

  if (!isVisible || !texture) {
    return null;
  }

  return (
    <PrecisePoints
      ref={setPointObject}
      position={position}
      material-size={
        isHovered || isPathHovered
          ? START_END_POINT_HOVERED_SIZE
          : START_END_POINT_SIZE
      }
      onClick={onClick}
      onContextMenu={onContextMenu}
      material-map={texture}
      material-transparent
      material-sizeAttenuation={false}
      material-depthTest={false}
      material-depthWrite
      name={`${index}`}
      renderOrder={SheetModeRenderOrders.StartEndWaypoints}
    >
      <SinglePointGeometry />
    </PrecisePoints>
  );
}

interface OtherPlaceholdersProps extends CommonProps {
  /** The set of placeholders to render */
  positions: Vector3[];

  /** The indices of the POIs within the positions array */
  poiIndices: number[];

  /** Additional placeholders that are rendered in the path and should be accounted for in the hiding logic. */
  references: Vector3[];

  /** The flags specifying which placeholders are locked */
  locked: boolean[];

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** The render order of the underlying MapWaypointsRenderer */
  renderOrder: number;

  /** Whether to show the auto-promoted waypoints */
  showPromoted: boolean;

  /** Whether to show the dense "other" waypoints */
  showOther: boolean;

  /** Whether to split middle waypoints into promoted and dense ones */
  enableAutoPromotion: boolean;

  /**
   * Callback executed when the hovered placeholder changes
   *
   * @param id the index of the hovered placeholder in the full path, undefined when none is hovered
   * @param kind the kind of waypoint reporting the change
   */
  onHoveredPlaceholderChanged(id: number | undefined, kind: WaypointKind): void;
}

const colorsToReplace = {
  "#8AC0FF": "red",
  "#7EA7F6": "red",
  "#E8A600": "red",
};

/**
 * @returns placeholders to be used in the middle of a path. Automatically hides cluttering waypoints.
 */
function OtherPlaceholders({
  positions,
  poiIndices,
  references,
  locked,
  onPlaceholderClick,
  onPlaceholderContextMenu,
  onHoveredPlaceholderChanged,
  hoveredId,
  clippingPlanes,
  renderOrder,
  isPathHovered,
  showPromoted,
  showOther,
  enableAutoPromotion,
}: OtherPlaceholdersProps): JSX.Element | null {
  const autoPromoteWaypointsRadius = useAppSelector(
    selectOdometryWaypointSpacing,
  );

  const [promotedIndices, otherIndices] = useMemo(() => {
    if (!enableAutoPromotion) {
      return [[], positions.map((_, index) => index)];
    }

    const promoted: number[] = [];
    const other: number[] = [];

    const radiusSq = autoPromoteWaypointsRadius * autoPromoteWaypointsRadius;
    positions.forEach((pos, index) => {
      // Promote only if no already-promoted waypoint sits within the radius,
      // keeping at most one promoted waypoint per neighborhood.
      const hasPromotedNeighbor = promoted.some(
        (ref) => pos.distanceToSquared(positions[ref]) < radiusSq,
      );
      if (hasPromotedNeighbor) {
        other.push(index);
      } else {
        promoted.push(index);
      }
    });
    return [promoted, other];
  }, [positions, autoPromoteWaypointsRadius, enableAutoPromotion]);

  // Both waypoint kinds are rendered from the full middle-waypoint array (so every per-waypoint
  // input stays in a single index space); each renderer force-hides the waypoints of the other kind.
  const isPromoted = useMemo(() => {
    const mask = positions.map(() => false);
    for (const index of promotedIndices) {
      mask[index] = true;
    }
    return mask;
  }, [positions, promotedIndices]);
  const isOther = useMemo(() => isPromoted.map((p) => !p), [isPromoted]);

  const defaultTexture = useSvg(PointSvg, {
    width: 256,
    height: 256,
    generateMipmaps: true,
    colorsToReplace,
  });
  const hoveredTexture = useSvg(HoverSvg, {
    width: 256,
    height: 256,
    generateMipmaps: true,
    colorsToReplace,
  });
  const lockedTexture = useSvg(LockedSvg, {
    width: 256,
    height: 256,
    generateMipmaps: true,
    colorsToReplace,
  });

  const alwaysVisible = useMemo(() => {
    const ret = [...locked];
    for (const poiIndex of poiIndices) {
      ret[poiIndex] = true;
    }
    return ret;
  }, [poiIndices, locked]);

  const {
    groupRef: promotedGroupRef,
    computeHiddenPlaceholders: computePromotedHidden,
  } = useSubSamplePlaceholders(
    // The start/end markers are only drawn with `showOther`, so don't reserve space for them otherwise.
    showOther ? references : [],
    PATH_WAYPOINTS_BASE_SIZE,
    alwaysVisible,
    clippingPlanes,
    isOther,
  );
  const {
    groupRef: otherGroupRef,
    computeHiddenPlaceholders: computeOtherHidden,
  } = useSubSamplePlaceholders(
    references,
    PATH_WAYPOINTS_BASE_SIZE,
    alwaysVisible,
    clippingPlanes,
    isPromoted,
  );

  const promotedHovered = useWaypointKindHoveredIndex(
    promotedIndices,
    hoveredId,
  );
  const otherHovered = useWaypointKindHoveredIndex(otherIndices, hoveredId);

  const promotedCallbacks = useWaypointKindCallbacks(
    "promoted",
    onPlaceholderClick,
    onPlaceholderContextMenu,
    onHoveredPlaceholderChanged,
  );
  const otherCallbacks = useWaypointKindCallbacks(
    "other",
    onPlaceholderClick,
    onPlaceholderContextMenu,
    onHoveredPlaceholderChanged,
  );

  const lockedPlaceholders = useMemo(() => {
    const middleLocked = [];
    for (let i = 0; i < locked.length; ++i) {
      if (locked[i]) {
        middleLocked.push(i);
      }
    }
    return middleLocked;
  }, [locked]);

  const [mapPlaceholders, setMapPlaceholders] =
    useState<MapWaypointsRendererRef | null>();
  const [promotedMapPlaceholders, setPromotedMapPlaceholders] =
    useState<MapWaypointsRendererRef | null>();

  const defaultTextures = useMapPlaceholderTextures(512, true, true);

  // A locked/POI waypoint can be auto-promoted, so both renderers need the same label styling.
  useLabeledWaypointStyles(
    mapPlaceholders,
    positions,
    lockedPlaceholders,
    lockedTexture,
    poiIndices,
    defaultTextures,
  );
  useLabeledWaypointStyles(
    promotedMapPlaceholders,
    positions,
    lockedPlaceholders,
    lockedTexture,
    poiIndices,
    defaultTextures,
  );

  const customColors = useMemo(() => {
    const colors = positions.map(
      (p, index) => new Color(index === otherHovered ? blue[300] : cyan[300]),
    );
    for (const index of poiIndices) {
      colors[index] = new Color(blue[500]);
    }
    return colors;
  }, [poiIndices, positions, otherHovered]);

  useEffect(() => {
    if (mapPlaceholders) mapPlaceholders.renderOrder = renderOrder;
    if (promotedMapPlaceholders) {
      promotedMapPlaceholders.renderOrder = renderOrder;
    }
  }, [mapPlaceholders, promotedMapPlaceholders, renderOrder]);

  return (
    <>
      {showPromoted && promotedIndices.length > 0 && (
        <group ref={promotedGroupRef}>
          <MapWaypointsRenderer
            waypoints={positions}
            hoveredSizeFactor={PLACEHOLDER_HOVER_FACTOR}
            hoveredId={promotedHovered}
            onPlaceholderClick={promotedCallbacks.onPlaceholderClick}
            onPlaceholderHovered={promotedCallbacks.onPlaceholderHovered}
            onPlaceholderContextMenu={
              promotedCallbacks.onPlaceholderContextMenu
            }
            computeHidden={computePromotedHidden}
            renderOrder={SheetModeRenderOrders.PathWaypoints}
            ref={setPromotedMapPlaceholders}
            generateMipmaps
            channelBlending
          />
        </group>
      )}

      {showOther && (
        <group ref={otherGroupRef}>
          <MapWaypointsRenderer
            waypoints={positions}
            baseSize={
              PATH_WAYPOINTS_BASE_SIZE *
              (isPathHovered ? PLACEHOLDER_HOVER_FACTOR : 1)
            }
            hoveredSizeFactor={PLACEHOLDER_HOVER_FACTOR}
            hoveredId={otherHovered}
            onPlaceholderClick={otherCallbacks.onPlaceholderClick}
            onPlaceholderHovered={otherCallbacks.onPlaceholderHovered}
            onPlaceholderContextMenu={otherCallbacks.onPlaceholderContextMenu}
            computeHidden={computeOtherHidden}
            textures={{
              defaultTexture,
              hoveredTexture,
            }}
            customColors={customColors}
            renderOrder={SheetModeRenderOrders.PathWaypoints}
            ref={setMapPlaceholders}
            generateMipmaps
            channelBlending
          />
        </group>
      )}
    </>
  );
}

type WaypointKindCallbacks = {
  /** Callback executed when one of the placeholders is clicked */
  onPlaceholderClick(index: number | undefined): void;

  /** Callback executed when a context menu is requested on one of the placeholders */
  onPlaceholderContextMenu(index: number | undefined): void;

  /** Callback executed when the hovered placeholder changes */
  onPlaceholderHovered(index: number | undefined): void;
};

/**
 * Applies the locked and POI label textures/sizes to a waypoints renderer. Both the promoted and the
 * dense renderer need it, since an auto-promoted locked/POI waypoint may be rendered by either one.
 *
 * @param mapPlaceholders the renderer to style, or null/undefined when it is not mounted
 * @param positions all middle waypoints, in the renderer's index space
 * @param lockedPlaceholders indices of the locked waypoints
 * @param lockedTexture texture used for the locked waypoints
 * @param poiIndices indices of the POI waypoints
 * @param poiTextures textures used for the POI waypoints
 */
function useLabeledWaypointStyles(
  mapPlaceholders: MapWaypointsRendererRef | null | undefined,
  positions: Vector3[],
  lockedPlaceholders: number[],
  lockedTexture: ReturnType<typeof useSvg>,
  poiIndices: number[],
  poiTextures: ReturnType<typeof useMapPlaceholderTextures>,
): void {
  // Assigning special sizes and textures to the locked waypoints
  useEffect(() => {
    if (!isMapPlaceholdersUpdated(mapPlaceholders, positions)) return;
    mapPlaceholders.setLabeledPlacehoders(
      LOCKED_WAYPOINTS_LABEL,
      lockedPlaceholders,
    );
    mapPlaceholders.setLabeledMap(
      LOCKED_WAYPOINTS_LABEL,
      PlaceholdersTexture.Default,
      lockedTexture,
    );
    mapPlaceholders.setLabeledMap(
      LOCKED_WAYPOINTS_LABEL,
      PlaceholdersTexture.Hovered,
      lockedTexture,
    );
    mapPlaceholders.setLabeledMap(
      LOCKED_WAYPOINTS_LABEL,
      PlaceholdersTexture.Selected,
      lockedTexture,
    );
    mapPlaceholders.setLabelSizes(LOCKED_WAYPOINTS_LABEL, {
      default: LOCKED_WAYPOINT_SIZE,
      hovered: LOCKED_HOVERED_WAYPOINT_SIZE,
      selected: LOCKED_HOVERED_WAYPOINT_SIZE,
    });
  }, [mapPlaceholders, lockedPlaceholders, lockedTexture, positions]);

  // Assigning special sizes and textures to the POI waypoints
  useEffect(() => {
    if (!isMapPlaceholdersUpdated(mapPlaceholders, positions)) return;
    mapPlaceholders.setLabeledPlacehoders(POI_HI_RES_LABEL, poiIndices);
    mapPlaceholders.setLabeledMap(
      POI_HI_RES_LABEL,
      PlaceholdersTexture.Default,
      poiTextures.defaultTexture,
    );
    mapPlaceholders.setLabeledMap(
      POI_HI_RES_LABEL,
      PlaceholdersTexture.Hovered,
      poiTextures.hoveredTexture,
    );
    mapPlaceholders.setLabeledMap(
      POI_HI_RES_LABEL,
      PlaceholdersTexture.Selected,
      poiTextures.selectedTexture,
    );
    mapPlaceholders.setLabelSizes(POI_HI_RES_LABEL, {
      default: DEFAULT_HI_RES_POI_SIZE,
      hovered: HOVERED_HI_RES_POI_SIZE,
      selected: HOVERED_HI_RES_POI_SIZE,
    });
  }, [mapPlaceholders, poiIndices, poiTextures, positions]);
}

/**
 * @param indices the middle waypoint index of each placeholder of the given kind
 * @param hoveredId the index of the hovered placeholder in the full path
 * @returns the middle waypoint index of the hovered placeholder, if it is one of the given kind
 */
function useWaypointKindHoveredIndex(
  indices: number[],
  hoveredId: number | undefined,
): number | undefined {
  return useMemo(() => {
    if (hoveredId === undefined) return;
    // The middle waypoints are offset by one, as the path starts with the start placeholder.
    const middleIndex = hoveredId - 1;
    return indices.includes(middleIndex) ? middleIndex : undefined;
  }, [indices, hoveredId]);
}

/**
 * @param kind the kind of waypoint the callbacks belong to
 * @param onClick callback for a click on a placeholder of the path
 * @param onContextMenu callback for a context menu on a placeholder of the path
 * @param onHovered callback for a hover change on a placeholder of the path
 * @returns the callbacks, translating the middle waypoint indices to indices in the full path
 */
function useWaypointKindCallbacks(
  kind: WaypointKind,
  onClick: ((index: number) => void) | undefined,
  onContextMenu: ((index: number) => void) | undefined,
  onHovered: (index: number | undefined, kind: WaypointKind) => void,
): WaypointKindCallbacks {
  return useMemo(
    () => ({
      // The middle waypoints are offset by one, as the path starts with the start placeholder
      onPlaceholderClick: (index: number | undefined): void => {
        if (index !== undefined) onClick?.(index + 1);
      },
      onPlaceholderContextMenu: (index: number | undefined): void => {
        if (index !== undefined) onContextMenu?.(index + 1);
      },
      onPlaceholderHovered: (index: number | undefined): void => {
        onHovered(index === undefined ? undefined : index + 1, kind);
      },
    }),
    [kind, onClick, onContextMenu, onHovered],
  );
}
